import type { ChartSelection } from "../types";

function SelectionControlGroup({
  selections,
  values,
  onSelectionChange,
}: {
  selections: ChartSelection[];
  values: Record<string, string>;
  onSelectionChange: (key: string, value: string) => void;
}) {
  if (!selections.length) {
    return null;
  }

  return (
    <div className="selection-control-group">
      {selections.map((selection) =>
        selection.options?.some((option) => option.path && option.path.length > 1) ? (
          <HierarchicalSelectionControl
            key={selection.key}
            selection={selection}
            value={values[selection.key] ?? selection.defaultValue}
            onChange={(value) => onSelectionChange(selection.key, value)}
          />
        ) : (
          <SingleSelectionControl
            key={selection.key}
            selection={selection}
            value={values[selection.key] ?? selection.defaultValue}
            onChange={(value) => onSelectionChange(selection.key, value)}
          />
        )
      )}
    </div>
  );
}

function SingleSelectionControl({
  selection,
  value,
  onChange,
}: {
  selection: ChartSelection;
  value: string;
  onChange: (value: string) => void;
}) {
  const options = selection.options?.length
    ? selection.options
    : selection.values.map((item) => ({ value: item, label: item }));

  return (
    <label className="selection-field">
      <span>{selectionLabel(selection.key)}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label ?? option.value}
          </option>
        ))}
      </select>
    </label>
  );
}

function HierarchicalSelectionControl({
  selection,
  value,
  onChange,
}: {
  selection: ChartSelection;
  value: string;
  onChange: (value: string) => void;
}) {
  const options = selection.options ?? [];
  const selectedPath = options.find((option) => option.value === value)?.path ?? [value];
  const selectedLevel1 = selectedPath[0] ?? "";
  const selectedLevel2 = selectedPath[1] ?? "";
  const selectedLevel3 = selectedPath[2] ?? "";
  const level1Options = uniqueOptions(
    options.filter((option) => option.path?.length === 1).map((option) => option.path?.[0] ?? option.value)
  );
  const level2Options = uniqueOptions(
    options
      .filter((option) => option.path?.[0] === selectedLevel1 && option.path.length === 2)
      .map((option) => option.path?.[1] ?? option.value)
  );
  const level3Options = uniqueOptions(
    options
      .filter((option) => option.path?.[0] === selectedLevel1 && option.path?.[1] === selectedLevel2 && option.path.length === 3)
      .map((option) => option.path?.[2] ?? option.value)
  );
  const labels = selection.levels ?? ["大分類", "中分類", "小分類"];

  return (
    <fieldset className="selection-fieldset">
      <legend>{selectionLabel(selection.key)}</legend>
      <label className="selection-field">
        <span>{labels[0] ?? "大分類"}</span>
        <select value={selectedLevel1} onChange={(event) => onChange(event.target.value)}>
          {level1Options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>
      {level2Options.length ? (
        <label className="selection-field">
          <span>{labels[1] ?? "中分類"}</span>
          <select value={selectedLevel2} onChange={(event) => onChange(event.target.value)}>
            <option value={selectedLevel1}>すべて</option>
            {level2Options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {level3Options.length ? (
        <label className="selection-field">
          <span>{labels[2] ?? "小分類"}</span>
          <select value={selectedLevel3} onChange={(event) => onChange(event.target.value)}>
            <option value={selectedLevel2}>すべて</option>
            {level3Options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </fieldset>
  );
}

function uniqueOptions(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)));
}

function selectionLabel(key: string): string {
  if (key.includes("area")) {
    return "エリア";
  }

  if (key.includes("item")) {
    return "費目";
  }

  if (key.includes("component")) {
    return "マインド指標";
  }

  if (key.includes("channel")) {
    return "流通チャネル";
  }

  if (key.includes("category")) {
    return "CPI分類";
  }

  return "表示対象";
}

export { SelectionControlGroup, SingleSelectionControl, HierarchicalSelectionControl, uniqueOptions, selectionLabel };
